import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Layout } from '~/components/UI';
import { LoadingTopBar } from '~/components/UI/LoadingTopBar';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { Feedbacks } from '~/containers/Feedbacks';
import { loadFeedbacks } from '~/containers/Feedbacks/actions';
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  FEEDBACKS_FILTERS_KEY,
  getDefaultFeedbacksFilters,
  getFeedbacksRequest,
  isFeedbacksPeriodDefault,
} from '~/containers/Feedbacks/helpers';

export const ScreensFeedbacks = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const isFirstLoad = useRef(true);

  // Filters kept across refreshes for a few minutes (per tenant/user/page).
  const persisted = usePersistedFilters(FEEDBACKS_FILTERS_KEY, getDefaultFeedbacksFilters);
  const applied = persisted.filters;

  // Loads the restored (or current-month) period on mount, and again when it changes.
  useEffect(() => {
    loadData();
  }, [applied.from, applied.to]);

  async function loadData() {
    const firstLoad = isFirstLoad.current;
    isFirstLoad.current = false;
    // The IndexedDB cache is keyed by route only, so it only ever holds the
    // default period; using it for another period would flash wrong data.
    const usesCache = isFeedbacksPeriodDefault(applied, persisted.defaults);

    try {
      if (firstLoad && usesCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.feedbacks,
          dispatch,
          pathname,
        });
      }
      const request = getFeedbacksRequest(applied);
      // First load keeps the top bar only (as before); later changes use the default loader.
      if (firstLoad) request.showLoading = false;
      const { payload } = await dispatch(loadFeedbacks(request));
      if (usesCache) {
        await LocalCacheHandler.save({
          pathname,
          data: payload.data.feedbacks,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <LoadingTopBar isActive={isLoading} />
      <Layout>
        <Feedbacks persisted={persisted} />
      </Layout>
    </div>
  );
};
